<script lang="ts" setup>
import Column from 'primevue/column'
import DataTable from 'primevue/datatable'
import StatusTag from '@/components/table/StatusTag.vue'
import TableSearchInput from '@/components/table/TableSearchInput.vue'
import {Currency, currencyOptions} from "@/constants/currency";
import {formatedDate} from "@/constants";
import {type PackingList, PackingListState} from "@/types/packingList-types";
import {onMounted, ref} from "vue";
import type {Product} from "@/types/product-types";
import type {CustomerOrderHistory} from "@/types/order-types";
import {getProductList} from "@/api/products";
import {getPackingList} from "@/api/packingList";

defineProps<{
  orderHistory: CustomerOrderHistory[]
  loading?: boolean
}>()

const EMPTY_GUID = '00000000-0000-0000-0000-000000000000';

const qualities = ref<Product[] | undefined>([])
const packingLists = ref<PackingList[] | undefined>([])
const formatCurrency = (amount: number | null | undefined, currencyType = Currency.GBP) => {
  if (amount === undefined || amount === null) return 'N/A'

  const currencySymbol = currencyOptions.find(option => option.value === currencyType)?.label || '£'
  return `${currencySymbol} ${parseFloat(amount.toString()).toFixed(2)}`
}
const getStatusText = (state: PackingListState, isCancelled?: boolean) => {
  if (isCancelled) {
    return 'Cancelled'
  }

  switch (state) {
    case PackingListState.Dispatched:
      return 'Dispatched'
    case PackingListState.Delivered:
      return 'Delivered'
    default:
      return 'Pending'
  }
}



const getQuality = (qualityId: string) => {
  return qualities.value?.find((quality) => quality.id === qualityId)?.finishedReference || 'Unknown Quality'
}

const getPackingListId = (packingListId: string) => {
  return packingLists.value?.find((packingList) => packingList.id === packingListId)?.packingListId || 'Unknown Packing List'
}

onMounted(async () => {
  qualities.value = await getProductList()
  packingLists.value = await getPackingList()
})

</script>

<template>
  <DataTable
    :value="orderHistory"
    class="border-t border-neutral-200/50 !rounded-t-3xl"
    tableStyle="min-width: 50rem"
    stripedRows
    :loading="loading"
    sortField="dateReceived"
    :sortOrder="-1"
  >
    <template #header>
      <div class="flex items-center justify-between font-normal pb-3">
        <div>
          <h2 class="text-xl font-semibold tracking-tight">Order History</h2>
          <p class="text-neutral-500 font-light text-sm">List of orders placed by this customer</p>
        </div>
        <TableSearchInput />
      </div>
    </template>
    <template #empty>
      <div class="text-center p-4 text-neutral-500">
        No order history found for this customer
      </div>
    </template>
    <Column field="orderNumber" header="Order Number" sortable>
      <template #body="slotProps">
        <RouterLink
            :to="`/orders/${slotProps.data.orderId}`"
            class="font-medium text-neutral-800 block hover:underline">
          {{ slotProps.data.orderNumber }}
        </RouterLink>
      </template>
    </Column>
    <Column field="dateReceived" header="Date" sortable>
      <template #body="slotProps">
        {{ formatedDate(slotProps.data.dateReceived) }}
      </template>
    </Column>
    <Column field="packingListId" header="Packing List ID" sortable >
      <template #body="slotProps">
        <template v-if="slotProps.data.packingListId && slotProps.data.packingListId !== EMPTY_GUID">
          <RouterLink
              :to="`/packingList/${(slotProps.data.packingListId)}`"
              class="font-medium text-neutral-800 block hover:underline">
            {{ getPackingListId(slotProps.data.packingListId) }}
          </RouterLink>
        </template>
        <template v-else>
          <span class="text-neutral-500">No Packing List</span>
        </template>
      </template>
    </Column>
    <Column field="qualityId" header="Quality" sortable >
      <template #body="slotProps">
        <RouterLink
            :to="`/productSpecification/${(slotProps.data.qualityId)}`"
            class="font-medium text-neutral-800 block hover:underline">
        {{ getQuality(slotProps.data.qualityId) }}
        </RouterLink>
      </template>
    </Column>
    <Column field="totalPriceAmount" header="Total Amount" sortable>
      <template #body="slotProps">
        {{ formatCurrency(slotProps.data.totalPriceAmount, slotProps.data.currency) }}
      </template>
    </Column>
    <Column field="packingListState" header="Status" sortable>
      <template #body="slotProps">
        <StatusTag
            :status="getStatusText(slotProps.data.packingListState, slotProps.data.isCancelled)"
        />
      </template>
    </Column>
  </DataTable>
</template>
<style></style>